<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="modelValue" class="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" @click="close" />

        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          leave-active-class="transition-all duration-200 ease-in"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <div
            v-if="modelValue"
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-dialog-title"
            class="relative bg-charcoal-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
            @click.stop
          >
            <!-- Gradient Header -->
            <div
              class="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 px-6 pt-6 pb-8 relative shrink-0"
            >
              <button
                class="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400 rounded-full p-1"
                aria-label="Close profile"
                data-testid="profile-dialog-close"
                @click="close"
              >
                <X :size="20" />
              </button>
              <div class="flex flex-col items-center text-center">
                <div class="relative">
                  <img
                    :src="user.picture || fallbackAvatar"
                    :alt="user.name || 'User avatar'"
                    class="w-20 h-20 rounded-full ring-4 ring-brand-400 shadow-lg object-cover bg-slate-600"
                    data-testid="profile-avatar"
                  />
                  <span
                    v-if="user.email_verified"
                    class="absolute -bottom-1 -right-1 bg-brand-400 rounded-full p-1"
                    title="Email verified"
                  >
                    <CheckCircle :size="12" class="text-white" />
                  </span>
                </div>
                <h2
                  id="profile-dialog-title"
                  class="mt-3 text-xl font-bold text-white tracking-tight"
                  data-testid="profile-name"
                >
                  {{ user.name || user.nickname || user.email }}
                </h2>
                <p class="text-slate-400 text-sm mt-0.5" data-testid="profile-email">
                  {{ user.email }}
                </p>
              </div>
            </div>

            <!-- Roles section -->
            <div class="px-6 py-4 border-b border-charcoal-700 shrink-0 space-y-3">
              <!-- Organisation Roles -->
              <div class="flex items-start gap-3">
                <p
                  class="text-[10px] font-semibold text-charcoal-400 uppercase tracking-widest w-32 shrink-0 pt-1"
                >
                  Org Roles
                </p>
                <div class="flex flex-wrap gap-1.5" data-testid="security-groups">
                  <span
                    v-if="ssoGroupsLoading"
                    class="text-xs text-charcoal-400 italic animate-pulse"
                    >Loading…</span
                  >
                  <template v-else>
                    <span
                      v-for="group in displayedSecurityGroups"
                      :key="group"
                      class="inline-flex items-center px-3 py-0.5 rounded text-xs font-medium bg-charcoal-800 border border-charcoal-600 text-charcoal-200"
                    >
                      {{ group }}
                    </span>
                    <span
                      v-if="displayedSecurityGroups.length === 0"
                      class="text-xs text-charcoal-400 italic"
                      >None assigned</span
                    >
                  </template>
                </div>
              </div>

              <!-- divider -->
              <div class="border-t border-charcoal-600" />

              <!-- Portal Roles -->
              <div class="flex items-start gap-3">
                <p
                  class="text-[10px] font-semibold text-charcoal-400 uppercase tracking-widest w-32 shrink-0 pt-1"
                >
                  Portal Roles
                </p>
                <div class="flex flex-wrap gap-1.5" data-testid="portal-roles">
                  <span
                    v-for="role in portalRoles"
                    :key="role"
                    class="inline-flex items-center px-3 py-0.5 rounded text-xs font-medium bg-charcoal-800 border border-charcoal-600 text-charcoal-200"
                  >
                    {{ role }}
                  </span>
                  <span v-if="portalRoles.length === 0" class="text-xs text-charcoal-400 italic"
                    >None assigned</span
                  >
                </div>
              </div>
            </div>

            <!-- Scrollable body -->
            <div class="overflow-y-auto flex-1 px-4 pb-4 pt-4 space-y-3">
              <!-- Permissions summary -->
              <div
                v-if="(user.permissions?.length ?? 0) > 0"
                class="bg-charcoal-900 rounded-xl px-4 py-3"
              >
                <div class="flex items-center justify-between mb-2">
                  <p
                    class="text-xs font-semibold text-charcoal-400 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <Key :size="13" /> Permissions
                    <span
                      class="ml-1 bg-brand-400/20 text-brand-600 px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                    >
                      {{ user.permissions?.length }}
                    </span>
                  </p>
                </div>
                <div class="flex flex-wrap gap-1">
                  <code
                    v-for="perm in user.permissions"
                    :key="perm"
                    class="text-[10px] bg-charcoal-800 border border-charcoal-600 text-charcoal-300 px-1.5 py-0.5 rounded font-mono"
                  >
                    {{ perm }}
                  </code>
                </div>
              </div>

              <!-- Info Dialog Preferences -->
              <div class="bg-charcoal-900 rounded-xl px-4 py-3">
                <p
                  class="text-xs font-semibold text-charcoal-400 uppercase tracking-wider flex items-center gap-1.5 mb-3"
                >
                  <Info :size="13" /> Info Dialog
                </p>
                <div class="space-y-3">
                  <!-- Show / hide toggle -->
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-charcoal-300">Show Info Dialog</span>
                    <button
                      role="switch"
                      :aria-checked="prefs.infoDialogVisible"
                      :class="[
                        'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2',
                        prefs.infoDialogVisible ? 'bg-brand-400' : 'bg-charcoal-600',
                      ]"
                      @click="prefs.infoDialogVisible = !prefs.infoDialogVisible"
                    >
                      <span
                        :class="[
                          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-charcoal-800 shadow ring-0 transition duration-200 ease-in-out',
                          prefs.infoDialogVisible ? 'translate-x-4' : 'translate-x-0',
                        ]"
                      />
                    </button>
                  </div>

                  <!-- Default state — only shown when dialog is enabled -->
                  <div v-if="prefs.infoDialogVisible" class="flex items-center justify-between">
                    <span class="text-sm text-charcoal-300">Default State</span>
                    <div
                      class="flex rounded-lg overflow-hidden border border-charcoal-600 text-xs font-medium"
                    >
                      <button
                        :class="[
                          'px-3 py-1 transition-colors',
                          prefs.infoDialogDefaultCollapsed
                            ? 'bg-brand-400 text-white'
                            : 'bg-charcoal-800 text-charcoal-300 hover:bg-charcoal-900',
                        ]"
                        @click="prefs.infoDialogDefaultCollapsed = true"
                      >
                        Collapsed
                      </button>
                      <button
                        :class="[
                          'px-3 py-1 transition-colors border-l border-charcoal-600',
                          !prefs.infoDialogDefaultCollapsed
                            ? 'bg-brand-400 text-white'
                            : 'bg-charcoal-800 text-charcoal-300 hover:bg-charcoal-900',
                        ]"
                        @click="prefs.infoDialogDefaultCollapsed = false"
                      >
                        Expanded
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Dev Console Preferences -->
              <div class="bg-gray-50 rounded-xl px-4 py-3">
                <p
                  class="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3"
                >
                  <Terminal :size="13" /> Dev Console
                </p>
                <div class="flex items-center justify-between">
                  <span class="text-sm text-gray-600">Show Dev Console</span>
                  <button
                    role="switch"
                    :aria-checked="prefs.devConsoleVisible"
                    :class="[
                      'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2',
                      prefs.devConsoleVisible ? 'bg-brand-400' : 'bg-gray-300',
                    ]"
                    @click="prefs.devConsoleVisible = !prefs.devConsoleVisible"
                  >
                    <span
                      :class="[
                        'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                        prefs.devConsoleVisible ? 'translate-x-4' : 'translate-x-0',
                      ]"
                    />
                  </button>
                </div>
              </div>

              <!-- Advanced Details Toggle -->
              <button
                class="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-charcoal-900 hover:bg-charcoal-700 transition-colors text-sm font-medium text-charcoal-300 group"
                data-testid="advanced-toggle"
                @click="toggleAdvanced"
              >
                <span class="flex items-center gap-2"
                  ><Bug :size="14" /> Debug / Advanced Details</span
                >
                <ChevronDown
                  :size="16"
                  class="text-charcoal-400 transition-transform duration-200"
                  :class="showAdvanced ? 'rotate-180' : ''"
                />
              </button>

              <Transition
                enter-active-class="transition-all duration-200 ease-out"
                leave-active-class="transition-all duration-150 ease-in"
                enter-from-class="opacity-0 -translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-2"
              >
                <div v-if="showAdvanced" class="space-y-3" data-testid="advanced-section">
                  <!-- Identity -->
                  <div class="bg-slate-800 rounded-xl px-4 py-3">
                    <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                      Identity
                    </p>
                    <div class="space-y-1.5">
                      <div class="flex gap-2">
                        <span class="text-xs text-slate-400 w-20 shrink-0">User ID</span>
                        <code class="text-xs text-brand-400 font-mono break-all">{{
                          user.sub
                        }}</code>
                      </div>
                      <div class="flex gap-2">
                        <span class="text-xs text-slate-400 w-20 shrink-0">Verified</span>
                        <span
                          :class="user.email_verified ? 'text-green-400' : 'text-red-400'"
                          class="text-xs"
                        >
                          {{ user.email_verified ? 'Yes' : 'No' }}
                        </span>
                      </div>
                      <div v-if="user.nickname" class="flex gap-2">
                        <span class="text-xs text-slate-400 w-20 shrink-0">Nickname</span>
                        <span class="text-xs text-slate-300">{{ user.nickname }}</span>
                      </div>
                      <div v-if="ssoInfo?.upn" class="flex gap-2">
                        <span class="text-xs text-slate-400 w-20 shrink-0">UPN</span>
                        <span class="text-xs text-slate-300 break-all">{{ ssoInfo.upn }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Organisation Groups -->
                  <div class="bg-slate-800 rounded-xl px-4 py-3" data-testid="organisation-groups">
                    <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                      Organisation Groups
                    </p>
                    <div
                      v-if="ssoGroupsLoading"
                      class="text-xs text-slate-500 animate-pulse italic"
                    >
                      Loading…
                    </div>
                    <div v-else-if="organisationGroups.length > 0" class="flex flex-wrap gap-1">
                      <code
                        v-for="group in organisationGroups"
                        :key="group"
                        class="text-[10px] text-brand-400 bg-slate-700 px-1.5 py-0.5 rounded font-mono"
                      >
                        {{ group }}
                      </code>
                    </div>
                    <span v-else-if="ssoGroupsFailed" class="text-xs text-slate-500 italic"
                      >Could not load organisation groups</span
                    >
                    <span v-else class="text-xs text-slate-500 italic">No organisation groups</span>
                  </div>

                  <!-- Claims -->
                  <div class="bg-slate-800 rounded-xl px-4 py-3">
                    <div class="flex items-center justify-between mb-2">
                      <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                        ID Token Claims
                      </p>
                      <span v-if="claimsLoading" class="text-xs text-slate-500 animate-pulse"
                        >Loading…</span
                      >
                    </div>
                    <div v-if="decodedClaims" class="overflow-x-auto">
                      <table class="w-full text-[10px] font-mono">
                        <tbody>
                          <tr
                            v-for="(value, key) in decodedClaims"
                            :key="key"
                            class="border-b border-slate-700 last:border-0"
                          >
                            <td class="py-1 pr-3 text-slate-400 align-top whitespace-nowrap">
                              {{ key }}
                            </td>
                            <td class="py-1 text-brand-400 break-all">
                              {{ formatClaimValue(value) }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p v-else-if="!claimsLoading" class="text-xs text-slate-500 italic">
                      Claims unavailable
                    </p>
                  </div>

                  <!-- Role sources -->
                  <div class="bg-slate-800 rounded-xl px-4 py-3">
                    <p class="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                      All Merged Roles (Auth0 + Portal DB)
                    </p>
                    <div class="flex flex-wrap gap-1">
                      <code
                        v-for="role in user.roles"
                        :key="role"
                        class="text-[10px] text-brand-400 bg-slate-700 px-1.5 py-0.5 rounded font-mono"
                      >
                        {{ role }}
                      </code>
                      <span v-if="!user.roles?.length" class="text-xs text-slate-500 italic"
                        >None</span
                      >
                    </div>
                  </div>

                  <!-- Access Token -->
                  <AuthTokenPanel />
                </div>
              </Transition>
            </div>

            <!-- Footer -->
            <div class="border-t border-charcoal-700 px-4 py-3 flex justify-end shrink-0">
              <button
                class="px-4 py-2 bg-brand-400 hover:bg-brand-500 text-white text-sm font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2"
                @click="close"
              >
                Close
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { X, CheckCircle, Key, ChevronDown, Bug, Info, Terminal } from 'lucide-vue-next'
import { useAuthStore } from '@stores/auth.store'
import { useUserPreferencesStore } from '@stores/user-preferences.store'
import type { Auth0GetUserInfoResponse } from '@/types/auth0-models'
import AuthTokenPanel from '@/components/AuthTokenPanel.vue'

const PROXY_ENABLED = String(import.meta.env.VITE_ENABLE_API_PROXY || '').toLowerCase()
const SC_API_BASE =
  PROXY_ENABLED === 'true' || PROXY_ENABLED === '1'
    ? // Same-origin '/api' is not proxied to the BFF by CloudFront in deployed envs; use the
      // absolute BFF base (matches every working service) and fall back to '/api' for local dev.
      (import.meta.env.VITE_BFF_URL as string | undefined) || '/api'
    : ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api')

const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{ (_e: 'update:modelValue', _v: boolean): void }>()

const authStore = useAuthStore()
const prefs = useUserPreferencesStore()
const user = computed(() => authStore.user!)
const showAdvanced = ref(false)
const claimsLoading = ref(false)
const decodedClaims = ref<Record<string, unknown> | null>(null)
const ssoInfo = ref<Auth0GetUserInfoResponse | null>(null)
const ssoGroupsLoading = ref(false)
const ssoGroupsFailed = ref(false)

const fallbackAvatar = `data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 40'><circle cx='20' cy='20' r='20' fill='%23475569'/><text x='50%25' y='55%25' dominant-baseline='middle' text-anchor='middle' fill='white' font-size='18' font-family='sans-serif'>?</text></svg>`

// Prefix-based matching so both 'SiteCompassAdmin' and 'SiteCompassAdmins' are handled
const isSiteCompassGroup = (name: string) => name.startsWith('SiteCompass')

// Only SiteCompass-prefixed groups shown at top; full list in Advanced Details
const displayedSecurityGroups = computed(() => {
  if (ssoInfo.value) return (ssoInfo.value.groups ?? []).filter(isSiteCompassGroup)
  if (ssoGroupsFailed.value) return (user.value?.roles ?? []).filter(isSiteCompassGroup)
  return []
})

const organisationGroups = computed(() => ssoInfo.value?.groups ?? [])

const portalRoles = computed(() => (user.value?.roles ?? []).filter(r => !isSiteCompassGroup(r)))

function formatClaimValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(', ')
  if (typeof value === 'object' && value !== null) return JSON.stringify(value)
  if (typeof value === 'number' && String(value as number).length === 10) {
    return `${value} (${new Date((value as number) * 1000).toLocaleString()})`
  }
  return String(value)
}

async function loadClaims() {
  if (decodedClaims.value) return
  claimsLoading.value = true
  try {
    const svc = authStore.getAuthService()
    if (!svc) return
    const result = await svc.getIdToken()
    if (!result.ok) return
    const payload = result.value.split('.')[1]
    if (!payload) return
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padding = (4 - (base64.length % 4)) % 4
    const padded = base64 + '='.repeat(padding)
    const decoded = JSON.parse(atob(padded))
    decodedClaims.value = decoded
  } catch {
    // silently fail — claims just won't show
  } finally {
    claimsLoading.value = false
  }
}

async function loadSsoGroups() {
  if (ssoInfo.value || ssoGroupsLoading.value || !user.value?.sub) return
  ssoGroupsLoading.value = true
  ssoGroupsFailed.value = false
  try {
    const tokenResult = await authStore.refreshToken()
    if (!tokenResult.ok) {
      ssoGroupsFailed.value = true
      return
    }
    const response = await fetch(`${SC_API_BASE}/GetUserInfo`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenResult.value}`,
      },
      body: JSON.stringify({ UserId: user.value.sub }),
    })
    if (!response.ok) {
      ssoGroupsFailed.value = true
      return
    }
    const result = (await response.json()) as Auth0GetUserInfoResponse
    if (result.is_error || result.error) {
      ssoGroupsFailed.value = true
      return
    }
    ssoInfo.value = result
  } catch {
    ssoGroupsFailed.value = true
  } finally {
    ssoGroupsLoading.value = false
  }
}

async function toggleAdvanced() {
  showAdvanced.value = !showAdvanced.value
  if (showAdvanced.value) await loadClaims()
}

function close() {
  emit('update:modelValue', false)
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') close()
}

watch(
  () => props.modelValue,
  open => {
    if (open) {
      loadSsoGroups()
    } else {
      showAdvanced.value = false
      ssoInfo.value = null
      ssoGroupsFailed.value = false
    }
  }
)

onMounted(() => {
  if (props.modelValue) loadSsoGroups()
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>
